/**
 * canMutate() — Run3-A central role-gating policy hook.
 *
 * Single source of truth for "can role R do action A on entity E?" decisions across
 * the frontend. Replaces 5+ disparate ad-hoc role checks scattered across
 * PositionTilesDashboard, PositionDetailPage, PositionAssigneesTab, PositionWizard
 * (+ steps), and UserManagementPage with one importable pure function.
 *
 * Plan 02-10a Task 2 (Run3-A + Run3-H + Run3-I + Run3-J close).
 *
 * Architecture:
 *  - Pure function, no React state, no API call, no async.
 *  - Hardcoded ROLE_POLICY dict — mirrors backend ``ROLE_PERMISSIONS`` in
 *    ``backend/app/core/rbac.py``. A1-locked 5-role permission map is the
 *    authoritative contract (architecture decision A1, design spec §10).
 *  - Defense-in-depth only — the backend ALWAYS enforces via
 *    ``require_perm(PERM_*)``. Frontend gates hide affordances + render
 *    read-only banners; they do NOT replace backend authorization.
 *
 * Matrix shape:
 *  - 5 entities × 4 actions × 5 roles = 100 cells.
 *  - Entities: position, template, question, assignment, user
 *  - Actions: create, edit, activate, delete
 *  - Roles: content_admin, super_admin, hr_manager, hr_recruiter, hiring_manager
 *
 * The matrix is intentionally redundant (boolean per cell) rather than a sparse
 * "permissions set per role" shape because: (a) it's grep-friendly — `grep
 * "position.*edit.*hr_manager"` returns the cell; (b) it forces every new
 * entity/action introduction to declare the policy for ALL 5 roles, preventing
 * partial coverage; (c) it makes the 100-cell test matrix trivially testable.
 *
 * Backend cross-check (Talenta 2026-05-27, Run 3 close):
 *  - content_admin: holds ALL write perms across position, template, question,
 *    user. Sole write actor for content + positions (USR-04 architecture).
 *  - super_admin: PERM_USER_LIST + PERM_POSITION_LIST + PERM_TEMPLATE_LIST +
 *    PERM_AUDIT_VIEW_ALL only — read-only on everything (privilege-escalation
 *    prevention; Stefan cannot edit users).
 *  - hr_manager: PERM_POSITION_ASSIGN_EDIT + PERM_USER_LIST +
 *    PERM_AUDIT_VIEW_OWN + read on templates/positions. Sole non-admin who can
 *    edit assignments (USR-03; cannot create users).
 *  - hr_recruiter / hiring_manager: list-only on positions (scoped by per-position
 *    user_positions join) + audit_view_own.
 *
 * Run 3 bug isolated 2026-05-26: PositionTilesDashboard had hr_manager in
 * CAN_CREATE_ROLES Set, surfacing "Nueva Posición" CTA — BUG (backend correctly
 * 403s; CTA produced dead-click failure mode). This module closes that BUG
 * structurally — hr_manager cannot create positions per ROLE_POLICY.
 */

export type Role =
  | "content_admin"
  | "super_admin"
  | "hr_manager"
  | "hr_recruiter"
  | "hiring_manager";

export type Entity =
  | "position"
  | "template"
  | "question"
  | "assignment"
  | "user"
  | "candidate" // Plan 03-07 — HR candidate review actions
  | "consent_version"; // Plan 03-08 — Ley 8968 consent-version editor (D7)

// Original CRUD-style verbs (position/template/question/assignment/user) PLUS the
// Plan 03-07 candidate lifecycle verbs. Not every entity uses every action — the
// matrix value type is Partial so an entity declares only the actions it supports
// (canMutate returns false for any undeclared cell).
export type Action =
  | "create"
  | "edit"
  | "activate"
  | "delete"
  | "view"
  | "advance"
  | "reject"
  | "unreject"
  | "write"; // Plan 03-08 — consent_version single-verb write (D7)

/**
 * 100-cell hardcoded ROLE_POLICY matrix.
 *
 * Structured as nested dict: entity -> action -> role -> boolean.
 *
 * Maintenance discipline: every cell is explicit (no fallthrough, no
 * default-deny shortcut at the matrix level — explicit ``false`` everywhere
 * makes the contract grep-discoverable and forces declarative review).
 */
export const ROLE_POLICY: Readonly<
  Record<Entity, Readonly<Partial<Record<Action, Readonly<Record<Role, boolean>>>>>>
> = {
  position: {
    create: {
      content_admin: true,
      super_admin: false,
      hr_manager: false,
      hr_recruiter: false,
      hiring_manager: false,
    },
    edit: {
      content_admin: true,
      super_admin: false,
      hr_manager: false,
      hr_recruiter: false,
      hiring_manager: false,
    },
    activate: {
      content_admin: true,
      super_admin: false,
      hr_manager: false,
      hr_recruiter: false,
      hiring_manager: false,
    },
    delete: {
      content_admin: true,
      super_admin: false,
      hr_manager: false,
      hr_recruiter: false,
      hiring_manager: false,
    },
  },
  template: {
    create: {
      content_admin: true,
      super_admin: false,
      hr_manager: false,
      hr_recruiter: false,
      hiring_manager: false,
    },
    edit: {
      content_admin: true,
      super_admin: false,
      hr_manager: false,
      hr_recruiter: false,
      hiring_manager: false,
    },
    activate: {
      // template archive/unarchive — only content_admin holds
      // PERM_TEMPLATE_ARCHIVE per backend ROLE_PERMISSIONS.
      content_admin: true,
      super_admin: false,
      hr_manager: false,
      hr_recruiter: false,
      hiring_manager: false,
    },
    delete: {
      // Talenta does not hard-delete templates; archive is the soft-delete.
      // Reserved as content_admin-only for symmetry with archive.
      content_admin: true,
      super_admin: false,
      hr_manager: false,
      hr_recruiter: false,
      hiring_manager: false,
    },
  },
  question: {
    create: {
      content_admin: true,
      super_admin: false,
      hr_manager: false,
      hr_recruiter: false,
      hiring_manager: false,
    },
    edit: {
      content_admin: true,
      super_admin: false,
      hr_manager: false,
      hr_recruiter: false,
      hiring_manager: false,
    },
    activate: {
      // questions don't have a separate activate; reserved as content_admin
      // for symmetry with create/edit.
      content_admin: true,
      super_admin: false,
      hr_manager: false,
      hr_recruiter: false,
      hiring_manager: false,
    },
    delete: {
      content_admin: true,
      super_admin: false,
      hr_manager: false,
      hr_recruiter: false,
      hiring_manager: false,
    },
  },
  assignment: {
    create: {
      // PERM_POSITION_ASSIGN_EDIT — both content_admin and hr_manager hold
      // this per ROLE_PERMISSIONS. USR-03: HR Manager assigns existing users
      // (no user-creation power).
      content_admin: true,
      super_admin: false,
      hr_manager: true,
      hr_recruiter: false,
      hiring_manager: false,
    },
    edit: {
      content_admin: true,
      super_admin: false,
      hr_manager: true,
      hr_recruiter: false,
      hiring_manager: false,
    },
    activate: {
      // Assignments don't have a separate activate state; mirrors create.
      content_admin: true,
      super_admin: false,
      hr_manager: true,
      hr_recruiter: false,
      hiring_manager: false,
    },
    delete: {
      content_admin: true,
      super_admin: false,
      hr_manager: true,
      hr_recruiter: false,
      hiring_manager: false,
    },
  },
  user: {
    create: {
      // PERM_USER_CREATE — content_admin only. USR-04 privilege-escalation
      // prevention: Stefan (super_admin) deliberately CANNOT create users.
      content_admin: true,
      super_admin: false,
      hr_manager: false,
      hr_recruiter: false,
      hiring_manager: false,
    },
    edit: {
      // PERM_USER_UPDATE — content_admin only.
      content_admin: true,
      super_admin: false,
      hr_manager: false,
      hr_recruiter: false,
      hiring_manager: false,
    },
    activate: {
      // PERM_USER_RESET_PW + activate/deactivate — content_admin only.
      content_admin: true,
      super_admin: false,
      hr_manager: false,
      hr_recruiter: false,
      hiring_manager: false,
    },
    delete: {
      // Talenta does not hard-delete users (Ley 8968 audit retention).
      // Soft-delete via is_active=false is content_admin-only.
      content_admin: true,
      super_admin: false,
      hr_manager: false,
      hr_recruiter: false,
      hiring_manager: false,
    },
  },
  // Plan 03-07 — HR candidate review (HR1-05/06). Mirrors backend admin_candidates.py
  // ALLOWED_HR_ROLES {content_admin, super_admin, hr_manager, hr_recruiter} for
  // view/advance/reject; REVERSE_REJECT_ROLES excludes hr_recruiter from unreject
  // (HR1-06). hiring_manager is finalist-only (Phase 4) — excluded from all four.
  candidate: {
    view: {
      content_admin: true,
      super_admin: true,
      hr_manager: true,
      hr_recruiter: true,
      hiring_manager: false,
    },
    advance: {
      content_admin: true,
      super_admin: true,
      hr_manager: true,
      hr_recruiter: true,
      hiring_manager: false,
    },
    reject: {
      content_admin: true,
      super_admin: true,
      hr_manager: true,
      hr_recruiter: true,
      hiring_manager: false,
    },
    unreject: {
      // HR1-06: reversibility restricted — hr_recruiter excluded (matches backend
      // REVERSE_REJECT_ROLES). Recruiters can advance/reject but not undo a reject.
      content_admin: true,
      super_admin: true,
      hr_manager: true,
      hr_recruiter: false,
      hiring_manager: false,
    },
  },
  // Plan 03-08 — Ley 8968 consent-version editor (D7 / 5.0k). Single 'write' verb.
  // Mirrors backend consent_versions.py: Content Admin ONLY writes (POST). Super-admin
  // READS (GET) but does NOT write — privilege-escalation prevention, same shape as
  // user/template/question editing. hr_* + hiring_manager never reach the surface.
  consent_version: {
    write: {
      content_admin: true,
      super_admin: false,
      hr_manager: false,
      hr_recruiter: false,
      hiring_manager: false,
    },
  },
};

/**
 * Returns whether the given role can perform the given action on the given entity.
 *
 * Defensive on unknown role or undefined: returns ``false`` rather than
 * throwing. Callers pass ``user?.role`` directly without a guard.
 *
 * @param entity One of the 5 entity classes.
 * @param action One of the 4 action types.
 * @param role The user's role string (typically ``user?.role`` from useAuthStore).
 *             Accepts ``undefined`` / ``null`` for the not-logged-in case.
 *
 * @returns true if the role holds the permission per ROLE_POLICY; false otherwise.
 *
 * @example
 *   const user = useAuthStore((s) => s.user);
 *   if (canMutate("position", "create", user?.role)) {
 *     // render Nueva Posición CTA
 *   }
 */
export function canMutate(
  entity: Entity,
  action: Action,
  role: Role | string | null | undefined,
): boolean {
  if (!role) return false;
  const entityPolicy = ROLE_POLICY[entity];
  if (!entityPolicy) return false;
  const actionPolicy = entityPolicy[action];
  if (!actionPolicy) return false;
  // Coerce string -> Role lookup; unknown roles yield undefined -> false.
  return actionPolicy[role as Role] === true;
}
